import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import ConfirmDialog from "../../../components/ConfirmDialog";
import { rulesApi } from "../../../utils/rulesApi";
import { isParkedGroup, rulesPath } from "../common";
import { useRuleStyles } from "../styles";
import { returnState } from "../../../utils/returnTo";

/**
 * Copy a rule set. Into the same group the server would answer 409 (the copy keeps the source
 * code), so that case opens the editor prefilled from the source and lets the admin pick a code
 * before anything is saved. Into another group the server copies and the editor opens on it.
 */
const CopySetDialog = ({ open, set, onClose }) => {
  const classes = useRuleStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [groups, setGroups] = useState([]);
  const [targetGroupId, setTargetGroupId] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    if (!open || !set) return;
    setTargetGroupId(String(set.ruleGroupId));
    setConflict(null);
    rulesApi
      .groups({
        page: 0,
        size: 200,
        sort: "Name",
        isAscending: true,
        status: "ACTIVE",
      })
      .then((page) =>
        setGroups((page?.items || []).filter((g) => !isParkedGroup(g))),
      )
      .catch((e) => toast.error(e.message));
  }, [open, set]);

  const copy = async () => {
    if (!set) return;
    if (String(set.ruleGroupId) === targetGroupId) {
      onClose();
      navigate(rulesPath.newSet(set.ruleGroupId, set.id), {
        state: returnState(location),
      });
      return;
    }
    setBusy(true);
    setConflict(null);
    try {
      const created = await rulesApi.copySet(set.id, Number(targetGroupId));
      toast.success(`Copied as rule set ${created.id}`);
      onClose();
      navigate(rulesPath.set(created.id), {
        state: returnState(location),
      });
    } catch (e) {
      if (e.status === 409 && e.details?.conflictingSetId) {
        setConflict(e.details);
      } else {
        toast.error(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      handleClose={onClose}
      title={set ? `Copy rule set · ${set.ruleName}` : "Copy rule set"}
      disableBackdropClose
    >
      <Box className={classes.dialogForm}>
        <Typography className={classes.muted}>
          The copy keeps every rule and the header of the source. Copying into
          the same group opens the editor so you can give the copy its own code
          first.
        </Typography>
        {conflict && (
          <Alert severity="error">
            {conflict.message}{" "}
            <RouterLink
              to={rulesPath.set(conflict.conflictingSetId)}
              state={returnState(location)}
            >
              Open rule set {conflict.conflictingSetId}
            </RouterLink>
          </Alert>
        )}
        <TextField
          select
          label="Copy into group"
          variant="outlined"
          size="small"
          value={targetGroupId}
          onChange={(e) => setTargetGroupId(e.target.value)}
        >
          {set && !groups.some((g) => g.id === set.ruleGroupId) && (
            <MenuItem value={String(set.ruleGroupId)}>This group</MenuItem>
          )}
          {groups.map((g) => (
            <MenuItem key={g.id} value={String(g.id)}>
              {g.name}
              {g.id === set?.ruleGroupId ? " (this group)" : ""}
            </MenuItem>
          ))}
        </TextField>
        <Box className={classes.dialogActions}>
          <Button className={classes.touch} onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            className={classes.touch}
            variant="contained"
            color="primary"
            onClick={copy}
            disabled={busy || !targetGroupId}
          >
            {set && String(set.ruleGroupId) === targetGroupId
              ? "Open copy in editor"
              : "Copy"}
          </Button>
        </Box>
      </Box>
    </ConfirmDialog>
  );
};

CopySetDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  set: PropTypes.shape({
    id: PropTypes.number,
    ruleGroupId: PropTypes.number,
    ruleName: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

CopySetDialog.defaultProps = { set: null };

export default CopySetDialog;
